<script lang="ts" setup>
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { useCategoryStore } from '~/store/categoryStore'
import { useIsLive } from '~/composables/states'

import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import { urlStatsDelimiter, FacetKeyEnum, CategoryIdEnum, SortDirection, PjCategoryIdEnum } from '~/types/searchTypes'
import type { CategoryTree } from '~/types/searchTypes'
import { FLAGS_FILTER_KEY, FACET_SUFFIX } from '~/composables/constants/search'
import QueryEnum from '~/enums/QueryEnum'

import getBrands from '~/composables/constants/brands'
import getDefaultCountryId from '~/utils/countries/getDefaultCountryId'

const categoryStore = useCategoryStore()
const route = useRoute()
const { t } = useLang()
const { isPerkuJavTheme } = useTheme()

const categoryIdsToDisplay = computed(() => {
  const idEnum = isPerkuJavTheme ? PjCategoryIdEnum : CategoryIdEnum

  return [idEnum.Electronics, idEnum.Clothes, idEnum.Footwear, idEnum.Accessories, idEnum.Cosmetics]
})
const brands = getBrands(isPerkuJavTheme ? PjCategoryIdEnum : CategoryIdEnum)

const isDealzone = computed(() => route.meta.layout === 'market-page')

const hoveredCategory = ref<CategoryTree>()
const isHovered = ref<boolean>(false)
let hoverTimeout: NodeJS.Timeout
const isLive = useIsLive(getDefaultCountryId() || 0)

const props = defineProps<{ showStores?: boolean }>()

const categoryItems = computed(() =>
  props.showStores
    ? []
    : categoryStore.getCategoryTree.filter((category) => categoryIdsToDisplay.value.includes(category.id))
)

const liveUrl = computed(() =>
  buildCategoryUrl('', false, {
    [`${FACET_SUFFIX}${FLAGS_FILTER_KEY}`]: 291,
  })
)

const salesUrl = computed(() =>
  buildCategoryUrl('', false, {
    s: 3,
    [`${FACET_SUFFIX}${FacetKeyEnum.DISCOUNT_PERCENTAGE}`]: [5, 100].join(urlStatsDelimiter),
  })
)

const showMegaMenu = computed(() => !props.showStores && isHovered.value && hoveredCategory.value?.id)

const toggleCategory = (category?: CategoryTree) => {
  hoveredCategory.value = category
}

const onCategoryHover = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = true
  }, 200)
}

const onCategoryLeave = () => {
  clearTimeout(hoverTimeout)
  isHovered.value = false
}

const offerLabels = computed(() => ({
  [SortDirection.PriceLowToHigh]: t('search.cheapest'),
  [SortDirection.BestValue]: t('search.best-value'),
  [SortDirection.BestSellers]: t('search.most-popular'),
  [SortDirection.NewArrivals]: t('search.new-arrivals'),
}))
</script>

<template>
  <div v-if="categoryItems.length" class="category-bar">
    <div class="nav-bar max-w-xlg w-full m-auto flex relative" :class="{ dealzone: isDealzone }">
      <ClientOnly>
        <Swiper
          slides-per-view="auto"
          :free-mode="true"
          :modules="[Navigation]"
          :navigation="true"
          :pagination="{ clickable: true, dynamicBullets: true }"
          :allow-touch-move="false"
          :prevent-clicks="false"
          :threshold="5">
          <SwiperSlide v-if="!props.showStores" key="all-products">
            <NuxtLink :to="buildCategoryUrl(null)" class="header-navbar-item">
              {{ t('common.all-products') }}
            </NuxtLink>
          </SwiperSlide>
          <SwiperSlide v-for="category in categoryItems" :key="category.id">
            <NuxtLink
              :to="buildCategoryUrl(category.slug)"
              :class="{ 'header-navbar-item': true, active: hoveredCategory?.id === category.id && showMegaMenu }"
              @mouseenter="() => {toggleCategory(category as CategoryTree); onCategoryHover()} "
              @mouseleave="onCategoryLeave()">
              {{ category.name }}
            </NuxtLink>
          </SwiperSlide>
          <SwiperSlide v-if="isLive" key="live">
            <NuxtLink :to="liveUrl" class="!text-brand-primary flex items-center gap-2 header-navbar-item">
              <span> {{ t('common.live') }} </span>
            </NuxtLink>
          </SwiperSlide>
          <SwiperSlide v-else key="biggest-discounts">
            <NuxtLink :to="salesUrl" class="!text-brand-primary flex items-center gap-2 header-navbar-item">
              <i class="fa-solid fa-percent text-[17px]"></i> <span> {{ t('common.sale') }} </span>
            </NuxtLink>
          </SwiperSlide>
        </Swiper>
      </ClientOnly>

      <div class="mega-menu" @mouseenter="isHovered = true" @mouseleave="isHovered = false">
        <div class="mega-menu-content hidden" :class="{ '!flex': showMegaMenu }">
          <section class="flex flex-col">
            <div class="section-title">
              {{ t('category.categories') }}
            </div>
            <NuxtLink
              v-for="cat in hoveredCategory?.children.slice(0, 8)"
              :key="cat.id"
              :to="buildCategoryUrl(cat.slug)">
              {{ cat.name }}
            </NuxtLink>
            <NuxtLink :to="buildCategoryUrl(null)" class="all-categories-link">
              {{ t('category.all-categories') }}
            </NuxtLink>
          </section>
          <hr />
          <section class="flex flex-col">
            <div class="section-title">{{ t('category.offers') }}</div>
            <NuxtLink
              v-for="(offer, index) in offerLabels"
              :key="index"
              :to="
                buildCategoryUrl(hoveredCategory?.slug, false, {
                  s: index,
                })
              ">
              {{ offer }}
            </NuxtLink>
          </section>
          <div v-if="hoveredCategory?.id">
            <div class="section-title">
              {{ t('category.most-popular-brands') }}
            </div>

            <div class="grid gap-4 grid-cols-2 xl:grid-cols-3">
              <div
                v-for="(item, index) in brands[hoveredCategory.id as CategoryIdEnum]"
                :key="`${item.name}-${index}`"
                class="flex">
                <NuxtLink
                  class="w-full"
                  :to="
                    buildCategoryUrl(
                      hoveredCategory.slug,
                      false,
                      {
                        [QueryEnum.Manufacturer]: item.name,
                      },
                      false
                    )
                  ">
                  <div class="brand">
                    <img :src="item.img" :alt="item.name" />
                  </div>
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.category-bar {
  @apply flex;

  .nav-bar {
    @apply z-20;
  }

  .section-title {
    @apply px-2 py-[0.5625rem] text-neutral4 uppercase text-lg font-bold dark:text-dmText1;
  }
}
</style>

<style lang="postcss">
.category-bar {
  @apply shadow-lg dark:shadow-dmGray2;

  hr {
    margin: 0 1.5rem;
    border-left: theme('vars.borderMedium');
    width: 1px;
    height: auto;
    border-color: theme('colors.neutral6');
  }

  .header-navbar-item {
    padding: 1rem;
    white-space: nowrap;
    color: theme('colors.gray7');
    text-decoration: none;
    border-bottom: 2px solid transparent;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.1em;

    @apply dark:text-dmText1;

    &:hover {
      color: theme('colors.brand.primary');
    }

    &.active {
      background-color: theme('colors.white');
      color: theme('colors.brand.primary');

      @apply dark:bg-dmGray1;
    }

    &.router-link-active {
      font-weight: 600;
    }
  }

  .dealzone {
    a.active {
      color: theme('colors.purple');
      border-bottom: 3px solid theme('colors.purple');
    }
  }

  .swiper {
    margin-left: 0;
    width: 100%;

    .swiper-slide {
      width: min-content !important;
      display: flex;

      &:last-of-type {
        justify-content: flex-end;
        flex: 1 0 auto;
      }
    }

    &-button-disabled {
      display: none;
    }

    .swiper-button-prev,
    .swiper-button-next {
      top: 50%;

      &::after {
        background-color: theme('colors.white');
        border: none;
        color: theme('colors.brand.primary');
        font-size: 1rem;
        box-shadow: theme('shadows.heavy');
      }
    }

    .swiper-button-prev {
      left: 0;
    }

    .swiper-button-next {
      right: 0;
    }
  }

  .mega-menu {
    position: absolute;
    top: 51px;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    background-color: theme('colors.white');
    box-shadow: theme('shadows.heavy');
    border-radius: theme('borderRadius.sm');

    @apply dark:bg-dmGray5;

    .mega-menu-content {
      padding: 1.25rem;
      height: 100%;
      font-weight: 500;
      font-size: 14px;
      width: 100%;
      z-index: 10;

      > section {
        max-width: 360px;
        width: 100%;

        a {
          font-weight: 500;
          text-transform: none;

          &.all-categories-link {
            color: theme('colors.brand.primary') !important;
            font-weight: 700;
          }
          &:hover {
            @apply underline;
          }
        }

        > * {
          padding: 0.75rem 0.5rem;
        }
      }
    }

    .column {
      padding: 0.5rem !important;
    }

    .brand {
      @apply bg-gray1 dark:bg-dmGray2;

      position: relative;
      padding: 1rem 1.375rem;
      border-radius: theme('vars.borderRadiusLight');
      height: 5rem;
      display: flex;

      img {
        height: 100%;
        width: 100%;
        object-fit: contain;
        @apply dark:invert;
      }

      &:hover {
        transition: all 0.1s linear;
        cursor: pointer;
        background-color: theme('colors.gray2');
        @apply dark:bg-dmGray5;
      }
    }
  }
}

.alt {
  .category-bar {
    .swiper {
      .swiper-button-prev,
      .swiper-button-next {
        &::after {
          box-shadow: none;
        }
      }
    }
  }
}
</style>
